// Generated by tutorials/record.cjs from tutorials/articles/h74-workflows.cjs.
// Do not edit: change the script and run `node tutorials/record.cjs h74 --text-only` (wording) or
// `node tutorials/record.cjs h74` (pictures and clips).
import type { HelpArticle } from '../types';

const article: HelpArticle = {
  slug: 'workflows',
  catalogId: 'h74',
  title: 'Workflows',
  summary: 'Your kitchen\'s own checklists: check off today\'s tasks meal by meal, set up the lists and their steps, and look back at what was done.',
  audience: 'Dietary managers, cooks and dietary staff',
  area: 'kitchen',
  learn: [
    'Check off today\'s tasks, meal by meal',
    'Open a step\'s page or report from the checklist',
    'Set up a checklist: its meal, its steps and which are required',
    'Shared lists and personal lists',
    'Look back at what was done on earlier days',
  ],
  keywords: [
    'workflow',
    'workflows',
    'checklist',
    'checklists',
    'task',
    'tasks',
    'to do',
    'opening checklist',
    'closing checklist',
    'tray line checklist',
    'kitchen tasks',
    'template',
    'templates',
    'required step',
    'shared',
    'personal',
    'history',
    'daily tasks',
  ],
  steps: [
    {
      id: 'today',
      part: 'Check off today\'s tasks',
      heading: 'Open Workflows',
      text: 'Select Kitchen in the menu on the left, then Workflows. The Today tab opens with today\'s date.\n\nEach card is one checklist, grouped by meal: Breakfast, Lunch, Dinner, Snack, then General for lists that are not tied to a meal. The bar and the count show how many steps are done, and the badge says Not Started, In Progress or Completed.',
      media: {
        image: {
          src: '/help/workflows/today.webp',
          width: 1280,
          height: 748,
        },
        alt: 'Workflows, Today: Breakfast tray line completed 4 of 4, Lunch service in progress 1 of 5, each with its steps listed.',
      },
    },
    {
      id: 'check',
      part: 'Check off today\'s tasks',
      heading: 'Check off a step',
      text: 'When a task is done, click the box beside it. The step is crossed through, the time it was checked shows beside it, and the count goes up. Everyone sees the same shared list, so the next person knows it is done.\n\nChecked one by mistake? Click the box again to clear it.',
      media: {
        image: {
          src: '/help/workflows/check.webp',
          width: 1280,
          height: 420,
        },
        clip: {
          src: '/help/workflows/check.mp4',
          width: 1280,
          height: 420,
          durationSec: 2.9,
        },
        alt: 'The Lunch service checklist with Print lunch tray tickets checked at the current time, and the count at 2 of 5, In Progress.',
      },
    },
    {
      id: 'action',
      part: 'Check off today\'s tasks',
      heading: 'Open the step\'s report or page',
      text: 'Some steps have a button on the right: a printer opens the step\'s report, and other icons go to a page, such as the Dashboard. Here, Review diet order changes since breakfast opens the Diet Order Changes report, ready to run.\n\nPoint at the button to see where it goes.',
      media: {
        image: {
          src: '/help/workflows/action.webp',
          width: 1280,
          height: 420,
        },
        alt: 'The printer button on Review diet order changes since breakfast, highlighted, beside its Required badge.',
      },
    },
    {
      id: 'complete',
      part: 'Check off today\'s tasks',
      heading: 'Finish the checklist',
      text: 'Once every step marked Required is checked, the checklist shows Completed and its bar turns green. Steps without the Required badge are nice to do: they never hold a checklist up.\n\nSelect a card\'s title to fold it away or open it again.',
      media: {
        image: {
          src: '/help/workflows/complete.webp',
          width: 1280,
          height: 386,
        },
        alt: 'The Breakfast tray line checklist, Completed, 4 of 4, with its four steps checked and the times they were checked.',
      },
    },
    {
      id: 'templates',
      part: 'Set up a checklist',
      heading: 'Open Templates',
      text: 'Select the Templates tab (or Manage Templates on Today).\n\nEach template is the pattern a checklist is made from every day. The row shows its meal, SHARED or PERSONAL, and how many steps it has. Select a template to change it; the trash can deletes it.',
      media: {
        image: {
          src: '/help/workflows/templates.webp',
          width: 1280,
          height: 748,
        },
        alt: 'Workflow Templates: Breakfast tray line, Lunch service and Dinner close-out marked SHARED, and Start of my shift marked PERSONAL, each with its step count.',
      },
    },
    {
      id: 'details',
      part: 'Set up a checklist',
      heading: 'Name the checklist',
      text: 'Select New Template. Type a Title, such as Afternoon snack pass, and pick the Meal Period it belongs to: Breakfast, Lunch, Dinner or Snack. Leave it empty for a list that is not about a meal, such as the start of a shift.\n\nAdd a Description so staff know when it applies.',
      media: {
        image: {
          src: '/help/workflows/details.webp',
          width: 1280,
          height: 378,
        },
        clip: {
          src: '/help/workflows/details.mp4',
          width: 1280,
          height: 378,
          durationSec: 6,
        },
        alt: 'New Workflow Template: Title Afternoon snack pass, Meal Period Snack, and the description Between lunch and dinner.',
      },
    },
    {
      id: 'shared',
      part: 'Set up a checklist',
      heading: 'Shared or personal',
      text: 'Shared (visible to all staff) makes one list for the whole kitchen: whoever does a task checks it off, and everyone sees it.\n\nPersonal copy per user gives each person their own copy to check off, such as a start of shift list. Turning one on turns the other off.',
      media: {
        image: {
          src: '/help/workflows/shared.webp',
          width: 1280,
          height: 378,
        },
        alt: 'The two switches, Shared (visible to all staff) on and Personal copy per user off.',
      },
    },
    {
      id: 'steps',
      part: 'Set up a checklist',
      heading: 'Add the steps',
      text: 'Under Steps, type the first task in Step title, and a Description if it needs one. Select Add Step for each next task.\n\nA step about a page or a report can open it from the checklist: set Action to Go to Page or Open Report, and pick which one. Here, Print nourishment labels opens the Nourishment - Labels report.',
      media: {
        image: {
          src: '/help/workflows/steps.webp',
          width: 1246,
          height: 376,
        },
        clip: {
          src: '/help/workflows/steps.mp4',
          width: 1246,
          height: 376,
          durationSec: 6,
        },
        alt: 'Three steps: Make the afternoon nourishments, Print nourishment labels with Action Open Report and Nourishment - Labels, and Deliver to the units.',
      },
    },
    {
      id: 'required',
      part: 'Set up a checklist',
      heading: 'Required, order and removing',
      text: 'Every new step starts as Required. Clear Required on a step that is nice to do but should not hold the list up: a checklist is Completed when its Required steps are done.\n\nThe arrows on the right move a step up or down, and the x removes it.',
      media: {
        image: {
          src: '/help/workflows/required.webp',
          width: 1246,
          height: 376,
        },
        alt: 'The third step, Deliver to the units, with Required checked and the up, down and remove buttons highlighted.',
      },
    },
    {
      id: 'save-template',
      part: 'Set up a checklist',
      heading: 'Save the template',
      text: 'Select Save Template. You are back on Templates with the new checklist on the list. Steps left with no title are not saved.',
      media: {
        image: {
          src: '/help/workflows/save-template.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/workflows/save-template.mp4',
          width: 1280,
          height: 748,
          durationSec: 3.2,
        },
        alt: 'Workflow Templates with Afternoon snack pass, Snack, SHARED, 3 steps, added to the list.',
      },
    },
    {
      id: 'on-today',
      part: 'Set up a checklist',
      heading: 'It\'s on Today',
      text: 'Select Today. The new checklist is there under Snack, ready to check off. A fresh, unchecked copy is made every day.',
      media: {
        image: {
          src: '/help/workflows/on-today.webp',
          width: 1280,
          height: 332,
        },
        clip: {
          src: '/help/workflows/on-today.mp4',
          width: 1280,
          height: 332,
          durationSec: 2.3,
        },
        alt: 'Workflows, Today with a Snack section holding Afternoon snack pass, Not Started, 0 of 3.',
      },
    },
    {
      id: 'history',
      part: 'Look back at history',
      heading: 'Open History',
      text: 'Select the History tab. It lists every step checked off between the two dates, newest first: the Date, the Workflow, the Meal, the step and its number, the Time it was checked and the checklist\'s Status that day.\n\nIt starts with the last seven days. Change the dates to look further back.',
      media: {
        image: {
          src: '/help/workflows/history.webp',
          width: 1280,
          height: 748,
        },
        alt: 'Workflow History for the last week: rows for Lunch service, Breakfast tray line and Dinner close-out steps, with dates, times and status.',
      },
    },
    {
      id: 'filter',
      part: 'Look back at history',
      heading: 'Look at one checklist',
      text: 'Pick a checklist in Workflow to see only its steps. Slide the list to the right to see Status. A day whose Status is In Progress is one where a required step was never checked off.\n\nThe search box above the list finds a step by its words.',
      media: {
        image: {
          src: '/help/workflows/filter.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/workflows/filter.mp4',
          width: 1280,
          height: 748,
          durationSec: 5.7,
        },
        alt: 'History filtered to Lunch service and slid to the right: the Status column shows an earlier day In Progress while the days around it are Completed.',
      },
    },
    {
      id: 'detail',
      part: 'Look back at history',
      heading: 'See a day\'s checklist',
      text: 'Click a row. Below the list, the checklist for that day shows its Date, Meal Period, Status and Progress, such as 3 / 5 steps. The X closes it.',
      media: {
        image: {
          src: '/help/workflows/detail.webp',
          width: 1280,
          height: 156,
        },
        clip: {
          src: '/help/workflows/detail.mp4',
          width: 1280,
          height: 156,
          durationSec: 2,
        },
        alt: 'The checklist detail under History: Lunch service, its date, Meal Period Lunch, Status In Progress, Progress 3 / 5 steps.',
      },
    },
  ],
  tips: [
    'Keep each checklist to the tasks people really skip or forget. Five good steps get checked; twenty get ignored.',
    'Give a step an action when it is about a page or a report, such as Print lunch tray tickets with Open Report, Tray Tickets. One click from the checklist opens it.',
    'Mark the steps that must happen as Required. A checklist shows Completed only when every Required step is checked, so optional steps never hold it up.',
  ],
  notes: [
    'A fresh list is made from each template every day. Yesterday\'s checks stay in History; Today always starts unchecked.',
    'Changing a template changes the lists from then on. Deleting a template takes it off Today; what was checked before stays in History.',
    'A template with neither Shared nor Personal copy per user turned on is not put on anyone\'s Today list. Keep one of them on.',
    'Everyone who can open Workflows can check off steps and change the templates.',
  ],
  related: ['tray-tickets', 'running-a-report', 'getting-around', 'your-dashboard'],
  recorded: '2026-11-03',
};

export default article;
